/* eslint-disable no-plusplus */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-len */
import React, {
  lazy, Suspense, useCallback, useEffect,
} from 'react';
import {
  Route,
  Routes,
  Skeleton,
  ROLE,
  eventBus,
  useTranslation,
  dayjs,
} from '@lib/index';
import * as Sentry from '@sentry/react';
import { useAuth0 } from '@auth0/auth0-react';
import { ENV } from '@config/envConfig';
import SwitchRole from '../_views/auth/SelectProfile/SwitchRole';
import MicrosoftEntraLogin from '../_views/auth/RedirectLogin/MicrosoftEntraLogin';
import OidcConsume from '../_views/auth/RedirectLogin/OidcConsume';

import Layout from '../_layout/Layout';
import PrivateRoute from './PrivateRoute';
import LoginForm from '../_views/auth/LoginForm';
import PasswordResetForm from '../_views/auth/PasswordResetForm';
import { persister } from '../_app/store';
import PasswordResetFormWithToken from '../_views/auth/PasswordResetForm/PasswordResetWithToken';
import ConfirmEmail from '../_views/auth/ConfirmEmail';
import Logout from '../_views/auth/Logout';
import '../_i18n/config';
import 'dayjs/locale/es';

const DashboardList = lazy(() => import('../_views/dashboard/DashboardList'));
const NotFound = lazy(() => import('../_views/pages/notfound/NotFound'));
const WorkerList = lazy(() => import('../_views/worker/WorkerList'));
const WorkerForm = lazy(() => import('../_views/worker/WorkerForm'));
const AccountDetail = lazy(() => import('../_views/account/AccountDetail'));
const AccountForm = lazy(() => import('../_views/account/AccountForm'));
const HelpCenterDetail = lazy(() => import('../_views/helpcenter/HelpCenterDetail'));
const HelpCenterVideoList = lazy(
  () => import('../_views/helpcenter/HelpCenterDetail/HelpCenterVideoList'),
);
const HelpCenterFileList = lazy(
  () => import('../_views/helpcenter/HelpCenterDetail/HelpCenterFileList'),
);
const AccessList = lazy(() => import('../_views/access/AccessList'));
const JobList = lazy(() => import('../_views/job/JobList'));
const JobDetail = lazy(() => import('../_views/job/JobDetail'));
const DocumentList = lazy(() => import('../_views/documentation/DocumentList'));
const DocumentAlert = lazy(() => import('../_views/documentalert/DocumentAlertList'));
const DocumentDetail = lazy(() => import('../_views/documentation/DocumentList/DocumentTab/DocumentDetail'));
const Setting = lazy(() => import('../_views/setting/SettingDetail'));
const SettingAccountForm = lazy(() => import('../_views/setting/SettingDetail/AccountTab/AccountForm'));
const ShiftForm = lazy(() => import('../_views/setting/SettingDetail/ShiftTab/ShiftForm'));
const PlacementForm = lazy(() => import('../_views/setting/SettingDetail/PlacementTab/PlacementForm'));
const MetaContractorSubcategoryForm = lazy(() => import('../_views/setting/SettingDetail/MetaContractorSubcategoryTab/MetaContractorSubcategoryForm'));
const UserList = lazy(() => import('../_views/user/UserList'));
const UserForm = lazy(() => import('../_views/user/UserForm'));
const UserDetail = lazy(() => import('../_views/user/UserDetail'));
const SiteWorkerDetail = lazy(() => import('../_views/siteworker/SiteWorkerDetail'));
const SiteWorkerList = lazy(() => import('../_views/siteworker/SiteWorkerList'));
const SiteWorkerForm = lazy(() => import('../_views/siteworker/SiteWorkerForm'));
const Unauthorized = lazy(() => import('../_views/pages/unauthorized/Unauthorized'));
const PersonnelList = lazy(() => import('../_views/personnel/PersonnelList'));
const PersonnelForm = lazy(() => import('../_views/personnel/PersonnelForm'));
const ReportingList = lazy(() => import('../_views/reporting/ReportingList'));
const ReportingDetail = lazy(() => import('../_views/reporting/ReportingDetail'));
const ContractorList = lazy(() => import('../_views/contractor/ContractorList'));
const ContractorDetail = lazy(() => import('../_views/contractor/ContractorDetail'));
const ContractorForm = lazy(() => import('../_views/contractor/ContractorForm'));
const ContractorUserForm = lazy(() => import('../_views/contractor/ContractorUserForm'));
const ContractorVehicleForm = lazy(() => import('../_views/documentation/ContractorVehicleForm'));
const StoreList = lazy(() => import('../_views/store/StoreList'));
const StoreDetail = lazy(() => import('../_views/store/StoreDetail'));
const StoreForm = lazy(() => import('../_views/store/StoreForm'));
const StoreUserForm = lazy(() => import('../_views/store/StoreUserForm'));
const AreaList = lazy(() => import('../_views/area/AreaList'));
const AreaForm = lazy(() => import('../_views/area/AreaForm'));
const AreaDetail = lazy(() => import('../_views/area/AreaDetail'));
const AreaUserForm = lazy(() => import('../_views/area/AreaUserForm'));
const JobPDFExport = lazy(() => import('../_views/job/JobDetail/JobPDFExport'));
const VehicleList = lazy(() => import('../_views/vehicle/VehicleList'));
const VehicleForm = lazy(() => import('../_views/vehicle/VehicleForm'));
const VehicleDetail = lazy(() => import('../_views/vehicle/VehicleDetail'));
const ContractorWorkerForm = lazy(() => import('../_views/contractor/ContractorWorkerForm'));
const LiveAccessTab = lazy(() => import('../_views/access/AccessList/LiveAccessTab'));
const ScheduleForm = lazy(() => import('../_views/setting/SettingDetail/WorkerReportTab/ScheduleForm'));
const WorkerDetail = lazy(() => import('../_views/worker/WorkerDetail'));

const LandingLayout = lazy(() => import('../_views/landing/components/LandingLayout'));
const LandingHome = lazy(() => import('../_views/landing/pages/Home'));
const LandingAbout = lazy(() => import('../_views/landing/pages/About'));
const LandingContact = lazy(() => import('../_views/landing/pages/Contact'));
const LandingProduct = lazy(() => import('../_views/landing/pages/Product'));

const LOGOUT_STORAGE_EXCLUDE_KEYS = [
  'job-pro-table-area_section',
  'job-pro-table-staffing_contractor',
  'job-pro-table-regular_contractor',
];

const clearLocalSession = () => {
  persister.purge();
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && !LOGOUT_STORAGE_EXCLUDE_KEYS.includes(key)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
};

type AppRoutesProps = {
  logoutFromAuth0?: () => Promise<void>;
};

const AppRoutes = ({ logoutFromAuth0 = undefined }: AppRoutesProps) => {
  const { i18n: { language } } = useTranslation();
  dayjs.locale(language);

  const handleLogOut = useCallback(async () => {
    if (logoutFromAuth0) {
      await logoutFromAuth0();
    }
    clearLocalSession();
  }, [logoutFromAuth0]);

  useEffect(() => {
    eventBus.on('logout', handleLogOut);
    return () => {
      eventBus.remove('logout', handleLogOut);
    };
  }, [handleLogOut]);

  return (
    <Routes>
      {/* Landing routes - public */}
      <Route element={<Suspense fallback={<Skeleton />}><LandingLayout /></Suspense>}>
        <Route index element={<Suspense fallback={<Skeleton />}><LandingHome /></Suspense>} />
        <Route path="about" element={<Suspense fallback={<Skeleton />}><LandingAbout /></Suspense>} />
        <Route path="contact" element={<Suspense fallback={<Skeleton />}><LandingContact /></Suspense>} />
        <Route path="product" element={<Suspense fallback={<Skeleton />}><LandingProduct /></Suspense>} />
      </Route>

      {/* Public Routes */}
      <Route path="login" element={<LoginForm />} />
      <Route path="reset-password" element={<PasswordResetForm />} />
      <Route path="recover-password/:token" element={<PasswordResetFormWithToken />} />
      <Route path="confirm-email" element={<ConfirmEmail />} />
      <Route path="logout" element={<Logout />} />
      <Route path="/microsoft/consume" element={<MicrosoftEntraLogin />} />
      <Route path="/oidc/consume" element={<OidcConsume />} />

      {/* Switch Role */}
      <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.store_section, ROLE.regular_contractor, ROLE.staffing_contractor, ROLE.corp_site, ROLE.administrator_section]} />}>
        <Route path="/switching/:id/:type" element={<SwitchRole />} />
      </Route>

      {/* Live Access */}
      <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.store_section, ROLE.regular_contractor, ROLE.staffing_contractor, ROLE.corp_site, ROLE.administrator_section]} permission="liveAccess" />}>
        <Route path="/live-access" element={<Suspense fallback={<Skeleton />}><LiveAccessTab /></Suspense>} />
      </Route>

      <Route path="/" element={<Layout />}>

        {/* Private Routes */}
        <Route path="login" element={<LoginForm />} />

        {/* Dashboard */}
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.regular_contractor, ROLE.corp_site, ROLE.area_section, ROLE.store_section, ROLE.administrator_section]} />}>
          <Route path="dashboard" element={<Suspense fallback={<Skeleton />}><DashboardList /></Suspense>} />
        </Route>

        {/* Worker */}
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.corp_site]} />}>
          <Route path="workers" element={<Suspense fallback={<Skeleton />}><WorkerList /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.corp_site]} />}>
          <Route path="workers/new" element={<Suspense fallback={<Skeleton />}><WorkerForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.corp_site]} />}>
          <Route path="workers/:id/edit" element={<Suspense fallback={<Skeleton />}><WorkerForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.corp_site]} />}>
          <Route path="workers/:id" element={<Suspense fallback={<Skeleton />}><WorkerDetail /></Suspense>} />
        </Route>

        {/* Account */}
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.area_section, ROLE.regular_contractor, ROLE.store_section, ROLE.administrator_section]} />}>
          <Route path="accounts" element={<Suspense fallback={<Skeleton />}><AccountDetail /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.area_section, ROLE.regular_contractor, ROLE.store_section, ROLE.administrator_section]} />}>
          <Route path="accounts/:id/edit" element={<Suspense fallback={<Skeleton />}><AccountForm /></Suspense>} />
        </Route>

        {/* Help Center */}
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.regular_contractor, ROLE.area_section, ROLE.store_section]} />}>
          <Route path="help-center" element={<Suspense fallback={<Skeleton />}><HelpCenterDetail /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.regular_contractor, ROLE.area_section, ROLE.store_section]} />}>
          <Route path="help-center/videos" element={<Suspense fallback={<Skeleton />}><HelpCenterVideoList /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.regular_contractor, ROLE.area_section, ROLE.store_section]} />}>
          <Route path="help-center/files" element={<Suspense fallback={<Skeleton />}><HelpCenterFileList /></Suspense>} />
        </Route>

        {/* Access */}
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.area_section, ROLE.regular_contractor, ROLE.administrator_section]} />}>
          <Route path="workers-accesses" element={<Suspense fallback={<Skeleton />}><AccessList /></Suspense>} />
        </Route>

        {/* Job */}
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.area_section, ROLE.regular_contractor, ROLE.store_section, ROLE.administrator_section]} />}>
          <Route path="jobs" element={<Suspense fallback={<Skeleton />}><JobList /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.regular_contractor, ROLE.area_section, ROLE.store_section, ROLE.administrator_section]} />}>
          <Route path="jobs/:id" element={<Suspense fallback={<Skeleton />}><JobDetail /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.area_section, ROLE.regular_contractor, ROLE.store_section, ROLE.administrator_section]} />}>
          <Route path="jobs/:id/export" element={<Suspense fallback={<Skeleton />}><JobPDFExport /></Suspense>} />
        </Route>

        {/* Reporting (KSS-2674): corporate profile only, no other role */}
        <Route element={<PrivateRoute roles={[ROLE.corp_site]} />}>
          <Route path="reporting" element={<Suspense fallback={<Skeleton />}><ReportingList /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.corp_site]} />}>
          <Route path="reporting/:id" element={<Suspense fallback={<Skeleton />}><ReportingDetail /></Suspense>} />
        </Route>

        {/* Contractor */}
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.administrator_section, ROLE.corp_site, ROLE.store_section]} />}>
          <Route path="contractors" element={<Suspense fallback={<Skeleton />}><ContractorList /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.administrator_section, ROLE.corp_site, ROLE.store_section]} />}>
          <Route path="contractors/new" element={<Suspense fallback={<Skeleton />}><ContractorForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.administrator_section, ROLE.corp_site, ROLE.store_section]} />}>
          <Route path="contractors/:id" element={<Suspense fallback={<Skeleton />}><ContractorDetail /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site, ROLE.store_section, ROLE.area_section, ROLE.administrator_section]} />}>
          <Route path="contractors/:id/users/new" element={<Suspense fallback={<Skeleton />}><ContractorUserForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.administrator_section, ROLE.store_section]} />}>
          <Route path="contractors/:id/edit" element={<Suspense fallback={<Skeleton active />}><ContractorForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section]} />}>
          <Route path="contractors/:id/vehicles/new" element={<Suspense fallback={<Skeleton />}><ContractorVehicleForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section]} />}>
          <Route path="contractors/:id/workers/new" element={<Suspense fallback={<Skeleton />}><ContractorWorkerForm /></Suspense>} />
        </Route>

        {/* Document */}
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.regular_contractor]} />}>
          <Route path="documentations" element={<Suspense fallback={<Skeleton />}><DocumentList /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.area_section, ROLE.regular_contractor, ROLE.administrator_section]} />}>
          <Route path="documentations/:id" element={<Suspense fallback={<Skeleton />}><DocumentDetail /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.regular_contractor]} permission="regularContractorF30" />}>
          <Route path="f30" element={<Suspense fallback={<Skeleton />}><DocumentList /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.regular_contractor]} />}>
          <Route path="contractors/vehicles/new" element={<Suspense fallback={<Skeleton />}><ContractorVehicleForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.area_section]} />}>
          <Route path="contractors/vehicles/:id/edit" element={<Suspense fallback={<Skeleton />}><ContractorVehicleForm /></Suspense>} />
        </Route>

        {/* Alert */}
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.staffing_contractor, ROLE.area_section]} />}>
          <Route path="alerts" element={<Suspense fallback={<Skeleton />}><DocumentAlert /></Suspense>} />
        </Route>

        {/* Settings */}
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site, ROLE.administrator_section]} />}>
          <Route path="settings" element={<Suspense fallback={<Skeleton />}><Setting /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site, ROLE.administrator_section]} />}>
          <Route path="/settings/accounts/:id/edit" element={<Suspense fallback={<Skeleton />}><SettingAccountForm /></Suspense>} />
        </Route>

        {/* Shifts */}
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site]} />}>
          <Route path="shifts/new" element={<Suspense fallback={<Skeleton />}><ShiftForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site]} />}>
          <Route path="shifts/:id/edit" element={<Suspense fallback={<Skeleton />}><ShiftForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site]} />}>
          <Route path="/placements/new" element={<Suspense fallback={<Skeleton />}><PlacementForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site]} />}>
          <Route path="/placements/:id/edit" element={<Suspense fallback={<Skeleton />}><PlacementForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site]} />}>
          <Route path="/meta-contractor-subcategories/new" element={<Suspense fallback={<Skeleton />}><MetaContractorSubcategoryForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site]} />}>
          <Route path="/meta-contractor-subcategories/:id/edit" element={<Suspense fallback={<Skeleton />}><MetaContractorSubcategoryForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site, ROLE.administrator_section]} />}>
          <Route path="/users" element={<Suspense fallback={<Skeleton />}><UserList /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site, ROLE.area_section, ROLE.administrator_section]} />}>
          <Route path="/users/new" element={<Suspense fallback={<Skeleton />}><UserForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site, ROLE.area_section, ROLE.administrator_section]} />}>
          <Route path="/users/:id/edit" element={<Suspense fallback={<Skeleton />}><UserForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site, ROLE.area_section, ROLE.administrator_section]} />}>
          <Route path="/users/:id/:type" element={<Suspense fallback={<Skeleton />}><UserDetail /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site, ROLE.area_section, ROLE.administrator_section]} />}>
          <Route path="/site-workers/:id" element={<Suspense fallback={<Skeleton />}><SiteWorkerDetail /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site, ROLE.area_section, ROLE.administrator_section]} />}>
          <Route path="/site-workers" element={<Suspense fallback={<Skeleton />}><SiteWorkerList /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site, ROLE.area_section, ROLE.administrator_section]} />}>
          <Route path="/site-workers/:id/edit" element={<Suspense fallback={<Skeleton />}><SiteWorkerForm /></Suspense>} />
        </Route>

        {/* Personnel */}
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.store_section, ROLE.administrator_section]} permission="personnel" />}>
          <Route path="/personnel" element={<Suspense fallback={<Skeleton />}><PersonnelList /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.store_section]} permission="personnel" />}>
          <Route path="/personnel/new" element={<Suspense fallback={<Skeleton />}><PersonnelForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.store_section]} permission="personnel" />}>
          <Route path="/personnel/:id/edit" element={<Suspense fallback={<Skeleton />}><PersonnelForm /></Suspense>} />
        </Route>

        {/* Store */}
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.store_section, ROLE.administrator_section]} permission="store" />}>
          <Route path="/stores" element={<Suspense fallback={<Skeleton />}><StoreList /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.store_section, ROLE.administrator_section]} permission="store" />}>
          <Route path="/stores/new" element={<Suspense fallback={<Skeleton />}><StoreForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.store_section, ROLE.administrator_section]} permission="store" />}>
          <Route path="/stores/:id/edit" element={<Suspense fallback={<Skeleton />}><StoreForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.store_section, ROLE.administrator_section]} permission="store" />}>
          <Route path="/stores/:id" element={<Suspense fallback={<Skeleton />}><StoreDetail /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site, ROLE.store_section, ROLE.area_section, ROLE.administrator_section]} />}>
          <Route path="/stores/:storeId/users/new" element={<Suspense fallback={<Skeleton />}><StoreUserForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site, ROLE.store_section, ROLE.area_section, ROLE.administrator_section]} />}>
          <Route path="/areas/:areaId/users/new" element={<Suspense fallback={<Skeleton />}><AreaUserForm /></Suspense>} />
        </Route>

        {/* Area &  Sub Contractor */}
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.store_section, ROLE.administrator_section]} />}>
          <Route path="/domain/:type" element={<Suspense fallback={<Skeleton />}><AreaList /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.store_section, ROLE.administrator_section]} />}>
          <Route path="/domain/:type/:typeId/new" element={<Suspense fallback={<Skeleton />}><AreaForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.store_section, ROLE.administrator_section]} />}>
          <Route path="/domain/:type/:id/edit" element={<Suspense fallback={<Skeleton />}><AreaForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section, ROLE.store_section, ROLE.administrator_section]} />}>
          <Route path="/domain/:type/:id" element={<Suspense fallback={<Skeleton />}><AreaDetail /></Suspense>} />
        </Route>

        {/* Vehicles */}
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section]} permission="sectionVehicles" />}>
          <Route path="/vehicles" element={<Suspense fallback={<Skeleton />}><VehicleList /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section]} permission="sectionVehicles" />}>
          <Route path="/vehicles/new" element={<Suspense fallback={<Skeleton />}><VehicleForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.area_section]} permission="sectionVehicles" />}>
          <Route path="/vehicles/:id/edit" element={<Suspense fallback={<Skeleton />}><VehicleForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.corp_site, ROLE.area_section, ROLE.administrator_section]} />}>
          <Route path="/vehicles/:id" element={<Suspense fallback={<Skeleton />}><VehicleDetail /></Suspense>} />
        </Route>

        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.administrator_section]} />}>
          <Route path="/schedules/new" element={<Suspense fallback={<Skeleton />}><ScheduleForm /></Suspense>} />
        </Route>
        <Route element={<PrivateRoute roles={[ROLE.admin, ROLE.administrator_section]} />}>
          <Route path="/schedules/:id/edit" element={<Suspense fallback={<Skeleton />}><ScheduleForm /></Suspense>} />
        </Route>

        {/* unauthorized */}
        <Route path="unauthorized" element={<Suspense fallback={<Skeleton />}><Unauthorized /></Suspense>} />

        {/* Catch all route */}
        <Route path="*" element={<Suspense fallback={<Skeleton />}><NotFound /></Suspense>} />
      </Route>
    </Routes>
  );
};

AppRoutes.defaultProps = {
  logoutFromAuth0: undefined,
};

const AppWithAuth0Wrapper = () => {
  const { logout } = useAuth0();

  return <AppRoutes logoutFromAuth0={logout} />;
};

const App = () => {
  const auth0Enabled = String(ENV.VITE_AUTH0_ENABLED ?? import.meta.env.VITE_AUTH0_ENABLED) === 'true';

  return auth0Enabled ? <AppWithAuth0Wrapper /> : <AppRoutes />;
};

export default Sentry.withProfiler(App);
